/**
 * Flutter Bridge
 * Handles communication between the web widget and Flutter WebView
 */

class FlutterBridge {
  constructor() {
    this.isFlutter = this.detectFlutter();
    this.messageHandlers = new Map();
    
    if (this.isFlutter) {
      console.log('🦋 Flutter WebView detected');
    }
  }

  /**
   * Detect if running inside a Flutter WebView
   * @returns {boolean}
   */
  detectFlutter() {
    // Check for Flutter-specific JavaScript channels
    return !!(
      window.FlutterChannel ||
      window.BiometricChannel ||
      window.flutter_inappwebview ||
      (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.FlutterChannel)
    );
  }

  /**
   * Send message to Flutter app
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  sendToFlutter(event, data) {
    const message = {
      event,
      data,
      timestamp: new Date().toISOString(),
    };

    console.log('📤 Sending to Flutter:', message);

    // Try different Flutter channel methods
    try {
      // Method 1: Direct FlutterChannel (Android)
      if (window.FlutterChannel && window.FlutterChannel.postMessage) {
        window.FlutterChannel.postMessage(JSON.stringify(message));
        return true;
      }

      // Method 2: BiometricChannel (custom channel name)
      if (window.BiometricChannel && window.BiometricChannel.postMessage) {
        window.BiometricChannel.postMessage(JSON.stringify(message));
        return true;
      }

      // Method 3: flutter_inappwebview (flutter_inappwebview package)
      if (window.flutter_inappwebview && window.flutter_inappwebview.callHandler) {
        window.flutter_inappwebview.callHandler('biometricEvent', message);
        return true;
      }

      // Method 4: iOS WebKit message handler
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.FlutterChannel) {
        window.webkit.messageHandlers.FlutterChannel.postMessage(message);
        return true;
      }

      // Method 5: Fallback - try to call a global function
      if (typeof window.onBiometricEvent === 'function') {
        window.onBiometricEvent(message);
        return true;
      }

      console.warn('⚠️ No Flutter channel available');
      return false;
    } catch (error) {
      console.error('❌ Error sending to Flutter:', error);
      return false;
    }
  }

  /**
   * Send success event to Flutter
   * @param {Object} data - Success data
   */
  sendSuccess(data) {
    this.sendToFlutter('biometric-success', data);
  }

  /**
   * Send error event to Flutter
   * @param {Object} error - Error data
   */
  sendError(error) {
    this.sendToFlutter('biometric-error', error);
  }

  /**
   * Send fallback event to Flutter
   * @param {Object} data - Fallback data
   */
  sendFallback(data) {
    this.sendToFlutter('biometric-fallback', data);
  }

  /**
   * Send cancel event to Flutter
   * @param {Object} data - Cancel data
   */
  sendCancel(data) {
    this.sendToFlutter('biometric-cancel', data);
  }

  /**
   * Send ready event to Flutter
   */
  sendReady() {
    this.sendToFlutter('biometric-ready', {
      ready: true,
      platform: 'web',
    });
  }

  /**
   * Listen for messages from Flutter
   * @param {Function} callback - Callback function
   */
  onMessageFromFlutter(callback) {
    // Method 1: Custom event listener
    window.addEventListener('flutterMessage', (event) => {
      console.log('📥 Message from Flutter:', event.detail);
      callback(event.detail);
    });

    // Method 2: Direct function call
    window.handleFlutterMessage = (message) => {
      console.log('📥 Message from Flutter:', message);
      callback(typeof message === 'string' ? JSON.parse(message) : message);
    };
  }

  /**
   * Notify Flutter that widget is loaded
   */
  notifyLoaded() {
    this.sendToFlutter('biometric-loaded', {
      loaded: true,
      url: window.location.href,
    });
  }

  /**
   * Send camera permission status to Flutter
   * @param {string} status - Permission status (granted, denied, prompt)
   */
  sendCameraPermission(status) {
    this.sendToFlutter('camera-permission', { status });
  }

  /**
   * Send progress update to Flutter
   * @param {number} progress - Progress percentage (0-100)
   * @param {string} message - Progress message
   */
  sendProgress(progress, message) {
    this.sendToFlutter('biometric-progress', { progress, message });
  }
}

// Export singleton instance
export default new FlutterBridge();
